import { useMemo, useState, useRef, useEffect } from 'react';
import {
  FiClock,
  FiBarChart2,
  FiActivity,
  FiUsers,
  FiCalendar,
  FiAlertCircle,
  FiCheckCircle,
  FiLoader,
  FiPauseCircle,
  FiTrendingUp,
} from 'react-icons/fi';
import { TimeUtilizationDataProps, TaskList } from '../../../types/task';
import EditTask from '../EditTask';
import { EditIcon } from '@chakra-ui/icons';
import { useUpdateTaskMutation } from '../../../features/apis/taskManager';

function getStatusClass(status: string): string {
  const map: Record<string, string> = {
    Completed: 'bg-green-100 text-green-700',
    'On track': 'bg-blue-100 text-blue-700',
    'In progress': 'bg-purple-100 text-purple-700',
    Delayed: 'bg-orange-100 text-orange-700',
    Pending: 'bg-gray-100 text-gray-500',
  };
  return map[status] ?? 'bg-gray-100 text-gray-500';
}

function getStatusIcon(status: string) {
  const iconClass = 'w-3 h-3 inline-block mr-1';
  switch (status) {
    case 'Completed':
      return <FiCheckCircle className={iconClass} />;
    case 'On track':
      return <FiTrendingUp className={iconClass} />;
    case 'In progress':
      return <FiLoader className={iconClass} />;
    case 'Delayed':
      return <FiAlertCircle className={iconClass} />;
    case 'Pending':
      return <FiPauseCircle className={iconClass} />;
    default:
      return null;
  }
}

const TASK_COLORS = [
  '#4f46e5',
  '#7c3aed',
  '#9333ea',
  '#6366f1',
  '#8b5cf6',
  '#a78bfa',
  '#c084fc',
  '#818cf8',
];

function getTaskColor(index: number): string {
  return TASK_COLORS[index % TASK_COLORS.length];
}

function StatusBadge({ status }: { status: string }) {
  return (
    <span
      className={`inline-flex items-center text-[10px] px-1.5 py-0.5 rounded-md whitespace-nowrap ${getStatusClass(
        status
      )}`}
    >
      {getStatusIcon(status)}
      {status}
    </span>
  );
}

export default function TimeUtilization({
  data,
  isLoading,
  refetch,
}: TimeUtilizationDataProps) {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [svgWidth, setSvgWidth] = useState(380);
  const [selectedTask, setSelectedTask] = useState<TaskList | null>(null);
  const [open, setOpen] = useState(false);
  const [updateTask, { isLoading: isUpdatingTask }] = useUpdateTaskMutation();
  const handleEditSubmit = async (
    updatedTask: import('../../../types/task').Task
  ) => {
    if (!selectedTask || !selectedTask._id) return;
    await updateTask({ _id: selectedTask._id, ...updatedTask }).unwrap();
    refetch?.();
  };

  const tasks = useMemo(() => data?.tasks ?? [], [data?.tasks]);
  const projectStatuses = useMemo(
    () => data?.projectStatuses ?? [],
    [data?.projectStatuses]
  );
  const onboardingData = useMemo(
    () => data?.onboardingData ?? [],
    [data?.onboardingData]
  );

  useEffect(() => {
    if (!wrapperRef.current) return;
    const ro = new ResizeObserver((entries) => {
      setSvgWidth(entries[0].contentRect.width || 380);
    });
    ro.observe(wrapperRef.current);
    return () => ro.disconnect();
  }, []);

  const totalDuration = useMemo(
    () => tasks.reduce((acc, t) => acc + (t.duration || 0), 0),
    [tasks]
  );

  const R = 140;
  const STROKE = Math.min(90, R * 0.42);
  const CX = svgWidth / 2;
  const CY = 180;

  const circ = 2 * Math.PI * R;
  const halfCirc = circ / 2;
  const START_ANGLE = 180;
  const startOffset = (circ * START_ANGLE) / 360;

  const segments = useMemo(() => {
    const GAP = 3;
    let cumulative = 0;
    return tasks.map((t) => {
      const fraction = totalDuration ? t.duration / totalDuration : 0;
      const dashLen = fraction * halfCirc - GAP;
      const dashGap = circ - dashLen;
      const offset = startOffset - cumulative;
      cumulative += fraction * halfCirc;
      return { dashLen: Math.max(0, dashLen), dashGap, offset };
    });
  }, [tasks, totalDuration, circ, halfCirc, startOffset]);

  const hoveredTask = hoveredIndex !== null ? tasks[hoveredIndex] : null;
  const svgHeight = CY + STROKE / 2 + 6;

  const maxOnboardingValue = useMemo(
    () => Math.max(...onboardingData.map((d) => d.value), 1),
    [onboardingData]
  );

  const truncateText = (text: string, maxLength: number = 26) => {
    if (!text) return '—';
    return text.length > maxLength ? text.slice(0, maxLength) + '...' : text;
  };

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
        <div className="bg-white rounded-xl shadow-lg p-4 space-y-3">
          <div className="flex items-center gap-2">
            <div className="h-4 w-4 bg-gray-200 rounded animate-pulse" />
            <div className="h-3 bg-gray-200 rounded-full animate-pulse w-24" />
          </div>
          <div className="w-[200px] h-[200px] mx-auto bg-gray-100 rounded-full animate-pulse" />
          <div className="space-y-2">
            <div className="h-2 bg-gray-100 rounded-full animate-pulse w-full" />
            <div className="h-2 bg-gray-100 rounded-full animate-pulse w-full" />
            <div className="h-2 bg-gray-100 rounded-full animate-pulse w-5/6" />
          </div>
        </div>
        <div className="grid grid-rows-2 gap-3">
          <div className="bg-white rounded-xl shadow-lg p-4 space-y-3">
            <div className="flex items-center gap-2">
              <div className="h-4 w-4 bg-gray-200 rounded animate-pulse" />
              <div className="h-3 bg-gray-200 rounded-full animate-pulse w-24" />
            </div>
            <div className="space-y-2">
              <div className="h-2 bg-gray-100 rounded-full animate-pulse w-full" />
              <div className="h-2 bg-gray-100 rounded-full animate-pulse w-full" />
              <div className="h-2 bg-gray-100 rounded-full animate-pulse w-4/6" />
            </div>
          </div>
          <div className="bg-white rounded-xl shadow-lg p-4 space-y-3">
            <div className="flex items-center gap-2">
              <div className="h-4 w-4 bg-gray-200 rounded animate-pulse" />
              <div className="h-3 bg-gray-200 rounded-full animate-pulse w-24" />
            </div>
            <div className="flex items-end justify-between gap-2 h-28">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="flex-1 bg-gray-100 rounded-sm animate-pulse" style={{ height: `${Math.max(12, Math.random() * 60)}px` }} />
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
      {/* ── Left panel: donut + table ── */}
        <div className="bg-white rounded-xl  p-4 shadow-lg">
        <div className="flex items-center gap-2 mb-3">
          <FiClock className="w-4 h-4 text-[#B9A5F0]" />
          <p className="text-xs font-medium text-[#B9A5F0] tracking-wide">
            Time spending
          </p>
        </div>
        <hr className="border-gray-100 border-[0.5px] my-2" />

        <div className="relative" ref={wrapperRef}>
          <svg
            width="100%"
            height={svgHeight}
            viewBox={`0 0 ${svgWidth} ${svgHeight}`}
            className="overflow-visible"
          >
            <circle
              cx={CX}
              cy={CY}
              r={R}
              fill="none"
              stroke="#f3e8ff"
              strokeWidth={STROKE}
              strokeDasharray={`${halfCirc} ${circ - halfCirc}`}
              strokeDashoffset={startOffset}
            />

            {tasks.map((task, i) => {
              const { dashLen, dashGap, offset } = segments[i];
              return (
                <circle
                  key={task.name + i}
                  cx={CX}
                  cy={CY}
                  r={R}
                  fill="none"
                  stroke={getTaskColor(i)}
                  strokeWidth={STROKE}
                  strokeDasharray={`${dashLen} ${dashGap}`}
                  strokeDashoffset={offset}
                  className="cursor-pointer transition-opacity duration-200"
                  style={{
                    opacity:
                      hoveredIndex !== null && hoveredIndex !== i ? 0.25 : 1,
                  }}
                  onMouseEnter={() => setHoveredIndex(i)}
                  onMouseLeave={() => setHoveredIndex(null)}
                />
              );
            })}

            <text
              x={CX}
              y={CY - 20}
              textAnchor="middle"
              style={{ fontSize: 18, fontWeight: 600, fill: '#1f2937' }}
            >
              {tasks.length}
            </text>
            <text
              x={CX}
              y={CY - 2}
              textAnchor="middle"
              style={{ fontSize: 10, fill: '#9ca3af' }}
            >
              Allocated Tasks{' '}
            </text>
          </svg>

          {hoveredTask && (
            <div
              className="absolute top-2 right-2 w-36 bg-white border-[0.5px] border-[#D4D4D4] rounded-xl p-2.5 z-10 pointer-events-none shadow-md"
              style={{ borderColor: getTaskColor(hoveredIndex!) }}
            >
              <p
                className="text-lg font-semibold mb-0.5"
                style={{ color: getTaskColor(hoveredIndex!) }}
              >
                {hoveredTask.duration} h
              </p>
              <p className="text-[11px] font-semibold text-gray-800 mb-0.5 line-clamp-2">
                {hoveredTask.name}
              </p>
              <p className="text-[10px] text-gray-400 mb-1.5">
                {hoveredTask.allocatedBy
                  ?.split(' ')
                  .map((n: string, i: number, arr: string[]) =>
                    i === arr.length - 1 ? `${n.charAt(0)}.` : n
                  )
                  .join(' ')}{' '}
                · {hoveredTask.allocatedDate}
              </p>
              <div className="mb-1.5">
                <StatusBadge status={hoveredTask.status} />
              </div>
              <div className="flex items-center">
                <div className="flex">
                  {hoveredTask.teamMembers.map((m, i) => (
                    <img
                      key={i}
                      src={m.avatar}
                      alt={m.name}
                      className="w-4 h-4 rounded-full border border-white -ml-1.5 first:ml-0 object-cover"
                    />
                  ))}
                </div>
                <span className="text-[9px] text-gray-400 ml-1.5 flex items-center gap-0.5">
                  <FiUsers className="w-3 h-3" />
                  {hoveredTask.teamMembers.length}
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Task table */}
        <div className="overflow-y-auto max-h-44 mt-2">
          <table className="w-full text-[10px] border-collapse">
            <thead className="sticky top-0 bg-white z-10 text-[10px]">
              <tr className="border-b-[0.5px] border-[#D4D4D4]">
                {[
                  { label: 'Task', icon: <FiActivity className="w-3 h-3" /> },
                  { label: 'Description', icon: null },
                  { label: 'People', icon: <FiUsers className="w-3 h-3" /> },
                  {
                    label: 'Status',
                    icon: <FiCheckCircle className="w-3 h-3" />,
                  },
                  {
                    label: 'Allocated',
                    icon: <FiCalendar className="w-3 h-3" />,
                  },
                  { label: 'Due', icon: <FiCalendar className="w-3 h-3" /> },
                  { label: 'Action', icon: <FiCalendar className="w-3 h-3" /> },
                ].map(({ label, icon }) => (
                  <th
                    key={label}
                    className="text-left py-1.5 px-1 text-[10px] font-medium text-[#444444]"
                  >
                    <span className="inline-flex items-center gap-1">
                      {icon}
                      {label}
                    </span>
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {tasks.length === 0 ? (
                <tr>
                  <td colSpan={6} className="py-8 text-center">
                    <div className="flex flex-col items-center gap-1.5">
                      <FiCalendar className="w-8 h-8 text-gray-200" />
                      <p className="text-[10px] font-medium text-gray-400">
                        No tasks yet
                      </p>
                      <p className="text-[10px] text-gray-300">
                        Add a task to get started
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                tasks.map((task, i) => (
                  <tr
                    key={i}
                    className="border-b-[0.5px] border-[#D4D4D4] last:border-0 cursor-pointer hover:bg-gray-50 transition-colors"
                  >
                    <td className="py-1.5 px-1 font-medium text-[10px]">
                      <div className="flex items-center gap-1.5">
                        <span
                          className="w-2 h-2 rounded-full flex-shrink-0"
                          style={{ background: getTaskColor(i) }}
                        />
                        {truncateText(task.name, 24)}
                      </div>
                    </td>
                    <td className="py-1.5 px-1 text-gray-600 capitalize text-[10px]">
                      {truncateText(task.description, 24)}
                    </td>
                    <td className="py-1.5 px-1 text-[10px]">
                      <div className="flex">
                        {task.teamMembers.map((m, j) => (
                          <img
                            key={j}
                            src={m.avatar}
                            alt={m.name}
                            className="w-4 h-4 rounded-full -ml-1 first:ml-0 border-2 border-white object-cover"
                          />
                        ))}
                      </div>
                    </td>
                    <td className="py-1.5 px-1 text-[10px]">
                      <StatusBadge status={task.status} />
                    </td>
                    <td className="py-1.5 px-1 text-gray-500 text-[10px]">
                      {truncateText(task.allocatedDate, 24)}
                    </td>
                    <td className="py-1.5 px-1 text-gray-500 text-[10px]">
                      {truncateText((task as any).dueDate ?? '—', 24)}
                    </td>
                    <td className="py-1.5 px-1 text-[10px]">
                      <EditIcon
                        className="w-3 h-3 text-gray-400 cursor-pointer hover:text-gray-600 transition-colors"
                        onClick={() => {
                          setSelectedTask(task);
                          setOpen(true);
                        }}
                      />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="grid grid-rows-2 gap-3">
        <div className="bg-white rounded-xl p-4 shadow-lg h-56">
          <div className="flex items-center gap-2 mb-1">
            <FiBarChart2 className="w-4 h-4 text-[#B9A5F0]" />
            <p className="text-xs font-medium text-[#B9A5F0]">Project status</p>
          </div>
          <hr className="border-gray-100 border-[0.5px] my-2" />

          {projectStatuses?.length > 0 ? (
            <div className="space-y-2">
              {projectStatuses.map((item, i) => (
                <div key={i} className="flex items-center gap-2">
                  <span className="text-[10px] w-7 text-right text-gray-400">
                    {item.value}%
                  </span>
                  <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all duration-500"
                      style={{
                        width: `${item.value}%`,
                        background: 'linear-gradient(to right, #C1ACFF, #E9E2FF)',
                      }}
                    />
                  </div>
                  <span className="text-[10px] w-20 text-gray-500 truncate">{item.label}</span>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-full text-center">
              <FiBarChart2 className="w-8 h-8 text-gray-300 mb-1.5" />
              <p className="text-[10px] text-gray-400">
                No project status available
              </p>
            </div>
          )}
        </div>
        <div className="bg-white rounded-xl  p-4 shadow-lg">
          <div className="flex items-center gap-2 mb-2">
            <FiActivity className="w-4 h-4 text-[#B9A5F0]" />
            <p className="text-xs font-medium text-[#B9A5F0]">
              Time distribution
            </p>
          </div>
          <hr className="border-gray-100 border-[0.5px] mb-3" />

          {onboardingData.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-28 text-center">
              <FiActivity className="w-8 h-8 text-gray-300 mb-1.5" />
              <p className="text-[10px] text-gray-400">No distribution data</p>
            </div>
          ) : (
            <div className="flex items-end justify-between gap-1.5 overflow-x-auto pb-6 w-full">
              {onboardingData.map((item, i) => {
                const BAR_MAX_PX = 80;
                const barHeightPx = Math.max(
                  (item.value / maxOnboardingValue) * BAR_MAX_PX,
                  4
                );
                return (
                  <div
                    key={i}
                    className="flex flex-col items-center flex-1"
                    style={{ minWidth: 0 }}
                  >
                    <p className="text-[10px] font-semibold text-gray-700 text-center mb-1">
                      {item.value}%
                    </p>

                    <div
                      className="transition-all duration-500 w-full rounded-sm"
                      style={{
                        maxWidth: '36px',
                        height: `${barHeightPx}px`,
                        background: item.color,
                      }}
                    />

                    <div
                      style={{
                        height: '42px',
                        width: '100%',
                        position: 'relative',
                        marginTop: '3px',
                        overflow: 'visible',
                      }}
                    >
                      <p
                        className="text-[9px] text-gray-400 leading-tight whitespace-nowrap absolute"
                        style={{
                          transform: 'rotate(40deg)',
                          transformOrigin: 'top left',
                          top: '2px',
                          left: '50%',
                        }}
                      >
                        {item.label}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {selectedTask && (
        <EditTask
          isOpen={open}
          onClose={() => setOpen(false)}
          task={selectedTask as any}
          onSubmit={handleEditSubmit}
          isLoading={isUpdatingTask}
        />
      )}
    </div>
  );
}
